import React, {ErrorInfo, Fragment, useCallback, useEffect, useState} from 'react';
import {connectAdvanced} from "src/store";
import {bindActionCreators} from "redux";
import {
    setEditorConfig,
    setFieldValue,
    setFormValues,
    setInitialValues,
    setPristineValues,
} from "../../../actions";
import {addEditor, createEditorReducer, REDUX_EDITOR_INIT} from "../../../reducers/editors";
import ComponentConfig from "../ComponentConfig";
import ComponentLoader from "../ComponentLoader";
import AskBeforeUnload from "./AskBeforeUnload";
import {postData} from "../../../utils/request";
import {get} from "../../../utils/dotutils";
import Button from "../Button";
import classNames from "classnames";
import {getComponentConfig} from "../../../utils/components";
import parse_str from "locutus/php/strings/parse_str";
import PropsProxy from "../../layout/PropsProxy";
import {ComponentConfigType} from "../../../utils/ComponentConfigType";
import AskBeforeDelete from "./AskBeforeDelete";
import {isValid, ValidationFieldType, ValidationType, validationTypeSort} from "./Validation";
import {list} from "../../../utils/array";
import Portal from "../../layout/Portal";
import Modal, {ModalProps} from "../../layout/Modal";
import debounce from "../../../utils/debounce";

type FilterType = {
    field?: string,
    value?: any,
    operator?: string|"or"|"and"|"OR"|"AND"|"="|"!="|"isnull"|"not isnull",
    matchmode?: string,
    expression?: string,
    filters?: FilterType[],
}

export type ComponentEditorPropsType<ValuesType extends {} = any> = {
    componentName: string,
    editorContext?: string,
    pristineValues?: Partial<ValuesType>,
    initialValues?: Partial<ValuesType>,
    dirty?: boolean,
    getFieldProps?: (name: string, props: any) => {[key: string]: any},
    presets?: Partial<ValuesType>,
    isLoading?: boolean,
    view?: string|object|string[]|object[],
    filter?: FilterType

    onFormLoaded?: (props: WrappedComponentEditorPropsType<ValuesType>) => void,
    valuesParser?: (props: WrappedComponentEditorPropsType<ValuesType>) => any,
    
    beforeUnloadPrompt?: React.ReactComponentElement<any>,
    askBeforeUnload?: boolean,

    loadForm?: (props: WrappedComponentEditorPropsType<ValuesType>&{data: ValuesType}) => any,
    onLoadForm?: (response: any) => void,

    saveForm?: (props: WrappedComponentEditorPropsType<ValuesType>&{requestData: {
        action: string,
        data: any,
        view: string|object|string[]|object[],
    }}) => any,
    onSaveForm?: (response: any) => void,

    beforeDeletePrompt?: React.ReactComponentElement<any>,
    askBeforeDelete?: boolean,
    deleteForm?: (props: WrappedComponentEditorPropsType<ValuesType>&{requestData: {
        action: string,
        [key: string]: any
    }}) => any,
    onDeleteForm?: (response: any) => void,

    actionValidation?: (props: {action: string, data: object, componentConfig: ComponentConfigType}) => Promise<ValidationType>,
    [otherProp: string]: any,
};

export type WrappedComponentEditorPropsType<ValuesType extends {} = any> = Partial<Omit<ComponentEditorPropsType, 'loadForm'> & {
    componentConfig: ComponentConfigType,
    values: ValuesType,
    setInitialValues: (data: ValuesType) => void,
    setFormValues: (data: ValuesType) => void,
    setFieldValue: (field: string, value: any) => void,
    reloadForm: () => Promise<void>,
    pristineForm: () => Promise<void>,
    loadForm: (data: ValuesType) => Promise<void>,
    saveForm: () => Promise<void>,
    deleteFrom: () => Promise<void>,
    getFieldProps: (field: string) => {[key: string]: any},
    getFieldsProps: (fields: string[]) => {[key: string]: any},
    processAction: () => Promise<void>,
    isLoading: boolean,
    saveButton: typeof React.Component,
    deleteButton: typeof React.Component,
    isValid: {[key: string]: boolean},
    validationMessages: ValidationType,
}>

export const referenceActionData = async (data: any, componentConfig: ComponentConfigType) => {
    let d = {...data};
    await Promise.all(Object.keys(componentConfig.fielddefs).map(async (fieldname) => {
        if (data.hasOwnProperty(fieldname)) {
            const type = (componentConfig.fielddefs[fieldname].type || "attribute");
            if (type === "object" && (data[fieldname] instanceof Object && data[fieldname] !== null)) {
                // @ts-ignore
                const config = await getComponentConfig(componentConfig.fielddefs[fieldname].object);
                d[fieldname] = data[fieldname][config.idfield];
            } else {
                d[fieldname] = data[fieldname];
            }
        }
    }));
    return d;
}

type ErrorState = {
    error?: null|Error,
    errorInfo?: null|ErrorInfo,
    collapsed?: boolean
}

class EditorErrorBoundary extends React.Component<any, ErrorState> {

    state: {
        error: any,
        errorInfo: any,
        collapsed: any,
    } = {
        error: null,
        errorInfo: null,
        collapsed: false
    }

    componentDidCatch(error: any, errorInfo: any) {
        // Catch errors in any child components and re-renders with an error message
        this.setState({
            error: error,
            errorInfo: errorInfo
        });
    }

    toggleCollapse = () => {
        this.setState({
            collapsed: !this.state.collapsed
        })
    }

    render() {
        if (this.state.error) {
            let error = this.state.error as Error;
            // Fallback UI if an error occurs
            return (
                <div className="box box-danger box-solid">
                    <div className="box-header with-border">
                        {error.toString()}
                        <div className="box-tools pull-right">
                            <Button onClick={this.toggleCollapse} type="button" className="btn btn-box-tool" data-widget="collapse" icon={{lib: "fa", name: classNames({"minus": !this.state.collapsed, "plus": this.state.collapsed})}} />
                        </div>
                    </div>
                    {!this.state.collapsed && <div className="box-body">
                        <div>Component Stack Error Details:</div>
                        <pre>{error.stack}</pre>
                        <pre>{this.state.errorInfo.componentStack}</pre>
                    </div>}
                </div>
            );
        }
        // component normally just renders children
        return this.props.children;
    }
}

const bindEditorActions = (editorName: string, bindActions: {[key: string]: any}) => {
    const actions: {[key: string]: () => any} = {};
    Object.keys(bindActions).map((actionName) => {
        actions[actionName] = function () {
            const args = [...Array.prototype.slice.call(arguments)];
            args.unshift(editorName);
            return bindActions[actionName].apply(null, args);
        }
    });
    return actions;
}

type ComponentValuesState = {
    isLoading?: boolean,
    isProcessing?: number,
    beforeUnloadPrompt?: null|{resolve: (reason: any) => void, reject: (reason: any) => void}
    beforeDeletePrompt?: null|{resolve: (reason: any) => void, reject: (reason: any) => void},
    validationMessages?: ValidationType,
    isValid?: {[key: string]: boolean},
    message?: null|ModalProps
}

type ComponentValuesProps = ComponentEditorPropsType & {
    innerRef: React.Ref<DefaultComponentEditor>,
    setContent?: (editor: string, props: object) => void,
    setParams?: (props: object) => void,
    componentConfig: ComponentConfigType,
    values: {[key: string]: any},
    setInitialValues: (data: any) => void,
    setFormValues: (data: any) => void,
    setFieldValue: (field: string, value: any) => void,
}

class ComponentValues extends React.Component<ComponentValuesProps, ComponentValuesState> {

    protected loader: ComponentLoader;

    state: {
        isLoading: boolean,
        isProcessing: number,
        beforeUnloadPrompt: any,
        beforeDeletePrompt: any,
        message: any,
        validationMessages: any,
        isValid: {[key: string]: any}
    } = {
        isLoading: false,
        isProcessing: 0,
        beforeUnloadPrompt: null,
        beforeDeletePrompt: null,
        message: null,
        validationMessages: null,
        isValid: {}
    }

    getFieldProps = (name: string) => {
        let props: {
            disabled?: boolean,
            required?: boolean,
        } = {};
        if (this.props.getFieldProps) {
            props = {...props, ...this.props.getFieldProps(name, this.props)};
        }
        if (this.props.presets) {
            const notExistsValue = "notExistsValue" + (new Date().getTime()) + "";
            const value = get(this.props.presets, name, notExistsValue);
            if (value !== notExistsValue) {
                props.disabled = true;
            }
        }
        if (this.props.componentConfig) {
            if (this.props.componentConfig.fielddefs) {
                if (this.props.componentConfig.fielddefs[name] && this.props.componentConfig.fielddefs[name].required) {
                    props.required = true;
                }
            }
        }
        if (this.isLoading()) {
            props.disabled = true;
        }
        let validationMessages = this.state.validationMessages as ValidationType;
        if (validationMessages?.fields) {
            const compileProps = (validation: ValidationFieldType[]) => {
                let validationMessage = validation.sort((a, b) => {
                    let ai = validationTypeSort.indexOf(a.type);
                    let bi = validationTypeSort.indexOf(b.type);
                    if (ai == bi) {
                        return a.priority == b.priority ? 0 : (a.priority > b.priority ? 1 : -1)
                    } else {
                        return ai > bi ? 1 : -1;
                    }
                })?.[0];
                if (validationMessage) {
                    props = {
                        ...props,
                        [validationMessage.type]: validationMessage.message
                    };
                }
            }
            if (validationMessages?.fields[name]) {
                const validation: ValidationFieldType[] = list(validationMessages?.fields[name]);
                compileProps(validation);
            } else {
                Object.entries<ValidationFieldType|ValidationFieldType[]>(validationMessages.fields).map(([fieldName, validMessage]) => {
                    // @ts-ignore
                    if (fieldName instanceof RegExp) {
                        if (fieldName.match(name)) {
                            const validation: ValidationFieldType[] = list(validMessage);
                            compileProps(validation);
                        }
                    }
                })
            }
        }
        return props;
    }

    getFieldsProps = (names: string[]) => {
        const props: {[key: string]: any} = {};
        names.map((name) => props[name] = this.getFieldProps(name));
        return props;
    }

    processAction = (action: (args: {}) => any) => {
        return this.loadingProcess(async () => {
            let values: any = this.props.values;
            let componentConfig = this.props.componentConfig;
            let result = null;
            if (action) {
                const componentAction = async (actionName: string, reparams = {}) => {
                    return postData({
                        action: componentConfig.componentname + '.' + actionName,
                        [componentConfig.idfield]: values[componentConfig.idfield],
                        ...reparams
                    });
                }
                const componentDataAction = async (actionName: string, reqparams = {}) => {
                    return postData({
                        action: componentConfig.componentname + '.' + actionName,
                        data: values,
                        ...reqparams
                    });
                }
                result = await action({componentAction, componentDataAction});
            }
            if (result === null || result === undefined || result || result.success) {
                return result;
            } else {
                throw result;
            }
        })
    }

    setInitialValues = async (data: any) => {
        await this.props.setInitialValues(data);
    }

    setFormValues = async (data: any) => {
        if (this.props.valuesParser) {
            try {
                // @ts-ignore
                data = this.props.valuesParser({values: data});
            } catch (e) {
                console.warn(e);
            }
        }
        if (data[this.props.componentConfig.idfield] + "" !== "" + this.props.values[this.props.componentConfig.idfield]) {
            await this.beforeUnload();
        }
        await this.props.setFormValues(data);
        await this.validateDebounce({action: this.saveActionType(), data: this.props.values, componentConfig: this.props.componentConfig});
    }

    saveForm = () => {
        return this.loadingProcess(async () => {
            const props = {...this.props};
            const componentConfig = this.props.componentConfig;
            let data: any = {...this.props.values, ...(this.props.presets || {})};
            let action = this.saveActionType({values: data, componentConfig});

            if (this.props.valuesParser) {
                try {
                    // @ts-ignore
                    data = this.props.valuesParser({values: data});
                } catch (e) {
                    throw new Error("not valid!");
                }
            }
            if (!(await this.validate({action, data, componentConfig}))) {
                throw new Error("not valid!");
            }

            let requestData: any = {
                action: props.componentName + "." + action,
                view: props.view || "default",
                data
            };
            if (data[componentConfig.idfield]) {
                requestData[componentConfig.idfield] = data[componentConfig.idfield];
            }
            let response;
            if (this.props.saveForm) {
                // @ts-ignore
                response = await this.props.saveForm({...props, requestData});
            } else {
                response = await postData(requestData, {}, {showError: false});
            }
            if (response.success) {
                this.setState({
                    message: {
                        theme: "success",
                        header: "Sikeres mentés",
                        body: "Sikeres mentés",
                        timeout: 2000,
                    }
                });
                if (response.data[componentConfig.idfield]) {
                    await this.setFieldValue(this.props.componentConfig.idfield, response.data[this.props.componentConfig.idfield]);
                    await this.setFormValues(response.data);
                    await this.loadForm(response.data);
                }
                try {
                    if(this.props.onSaveForm) {
                        await this.props.onSaveForm(response);
                    }
                } catch (e) {
                    console.error(e);
                }
                return response.data;
            } else {
                throw response;
            }
        });
    }

    deleteForm = async () => {
        return this.loadingProcess(async () => {
            await this.beforeDelete();
            const props = {...this.props};
            const componentConfig = this.props.componentConfig;
            let data = {...this.props.values, ...(this.props.presets || {})};
            let action = "delete";

            if (!(await this.validate({action, data, componentConfig}))) {
                throw new Error("not valid!");
            }

            const id = data[componentConfig.idfield];
            if (id) {
                const requestData = {
                    action: props.componentName + "." + action,
                    [componentConfig.idfield]: id,
                };
                let response;
                if (this.props.deleteForm) {
                    // @ts-ignore
                    response = await this.props.deleteForm({...props, requestData});
                } else {
                    response = await postData(requestData, {}, {showError: false});
                }
                if (response.success) {
                    try {
                        if (this.props.onDeleteForm) {
                            await this.props.onDeleteForm(response);
                        }
                    } catch (e) {
                        console.error(e);
                    }
                    await this.pristineForm();
                } else {
                    console.error(response);
                    throw response;
                }
            }
        });
    }

    saveActionType = ({values, componentConfig}: {values?: {[key: string]: any}, componentConfig?: ComponentConfigType} = {}) => {
        values = values || this.props.values;
        componentConfig = componentConfig || this.props.componentConfig;
        return values[componentConfig.idfield] ? "update" : "create";
    }

    isLoading = () => {
        return this.props.isLoading || this.state.isLoading || !!this.state.isProcessing;
    }

    reloadForm = async () => {
        if (this.loader && this.props.values[this.props.componentConfig.idfield]) {
            const values = await this.loader.loadComponent();
            await this.setFormValues(values);
        }
    }

    pristineForm = () => {
        return this.loadingProcess(async () => {
            await this.beforeUnload();
            await this.setFormValues(this.props.pristineValues);
            await this.loadForm(this.props.pristineValues);
        });
    }

    loadForm = (data: any) => {
        return this.loadingProcess(async () => {
            await this.beforeUnload();
            if (data?.[this.props.componentConfig.idfield] + "" !== "" + this.props[this.props.componentConfig.idfield]) {
                if (this.props.loadForm) {
                    // @ts-ignore
                    await this.props.loadForm({...this.props, data});
                }
                try {
                    if(this.props.onLoadForm) {
                        await this.props.onLoadForm(data);
                    }
                } catch (e) {
                    console.error(e);
                }
            }
        });
    }

    beforeUnload = () => {
        if (this.props.askBeforeUnload === undefined || this.props.askBeforeUnload === true) {
            return new Promise<void>((resolve, reject) => {
                if (this.props.dirty) {
                    this.setState({beforeUnloadPrompt: {resolve,reject}});
                } else {
                    resolve();
                }
            });
        } else {
            return Promise.resolve();
        }
    }
    
    beforeDelete = () => {
        if (this.props.dirty) return Promise.reject();
        if (this.props.askBeforeDelete === undefined || this.props.askBeforeDelete === true) {
            return new Promise((resolve, reject) => {
                this.setState({beforeDeletePrompt: {resolve,reject}});
            });
        } else {
            return Promise.resolve();
        }
    }

    loadingProcess = (call: () => Promise<any>) => {
        return new Promise((resolve, reject) => {
            this.setState(function(state) {
                return {
                    isProcessing: state.isProcessing + 1
                };
            }, async () => {
                try {
                    const result: {success?: boolean, newWindow?: string, redirect?: string} = await call();
                    if (result?.success) {
                        if (result.newWindow) {
                            window.open(result.newWindow, '_blank');
                        }
                        if (result.redirect) {
                            let params: {option?: string} = {};
                            parse_str(result.redirect.substr(1), params);
                            if (this.props.setContent && params.option) {
                                this.props.setContent(params.option, params);
                            } else {
                                console.warn(`No redirect option parameter!`);
                            }
                        }
                    }
                    this.setState(function(state) {
                        return {
                            isProcessing: state.isProcessing - 1
                        };
                    }, () => {
                        resolve(result);
                    });
                } catch (e) {
                    this.setState(function(state: {
                        isProcessing: number,
                        message?: any
                    }) {
                        state = {
                            isProcessing: state.isProcessing - 1
                        };
                        if (e?.msg) {
                            state.message = {
                                theme: "danger",
                                header: "Hiba történt",
                                body: e.msg
                            };
                        } else if (e?.validation) {
                            state.message = {
                                size: "large",
                                theme: "warning",
                                header: "Sikertelen művelet",
                                body: <div>
                                    {Object.keys(e.messages || {}).map((key) => {
                                        if (Array.isArray(e.messages[key])) {
                                            return e.messages[key].map((message: any, idx: number) => {
                                                return <div key={idx}>{message}</div>;
                                            })
                                        }
                                        return <div>{e.messages[key]}</div>;
                                    })}
                                </div>
                            }
                        }
                        return state;
                    }, () => {
                        reject(e);
                    });
                }
            });
        });
    }

    onBeforeLoad = () => {
        if (this.props[this.props.componentConfig.idfield] + "" !== "" + this.props.values[this.props.componentConfig.idfield]) {
            this.setState({isLoading: true});
        }
    }

    onLoadCompleted = async (values: any) => {
        if (!this.props[this.props.componentConfig.idfield] && !this.props.filter && !values[this.props.componentConfig.idfield]) {
            values = this.props.pristineValues;
        }
        if (values[this.props.componentConfig.idfield] + "" !== "" + this.props.values[this.props.componentConfig.idfield]) {
            try {
                await this.setFormValues(values);
            } catch (e) {
                await this.loadForm(this.props.values);
            }
            if (this.props.onFormLoaded) {
                // @ts-ignore
                await this.props.onFormLoaded(this.props);
            }
            await this.validate({action: this.saveActionType(), data: this.props.values, componentConfig: this.props.componentConfig});
        }
        this.setState({isLoading: false});
    }

    saveButton = (props: any) => {
        const isLoading = this.isLoading();
        return (
            <Button
                color={this.state.isValid[this.saveActionType()] === false ? "danger" : (this.props.dirty ? "primary" : "default")}
                className="pull-right"
                onClick={props.saveForm || this.saveForm}
                {...props}
                disabled={!this.props.dirty || props.isLoading || isLoading || props.disabled}
            >
                {props.children || "Mentés"}
            </Button>
        );
    }

    newButton = (props: any) => {
        const isLoading = this.isLoading();
        return (
            <Button
                color="default"
                className="pull-left"
                onClick={props.pristineForm || this.pristineForm}
                {...props}
                disabled={props.isLoading || isLoading || props.disabled}
            >
                {props.children || "Új"}
            </Button>
        );
    }

    deleteButton = (props: any) => {
        const isLoading = this.isLoading();
        return (
            <Button
                color={this.state.isValid[this.saveActionType()] === false ? "default" : "danger"}
                className="pull-left"
                onClick={props.deleteForm || this.deleteForm}
                {...props}
                disabled={props.disabled || !this.props.values[this.props.componentConfig.idfield] || props.isLoading || isLoading}
            >
                {props.children || "Törlés"}
            </Button>
        );
    }

    validateDebounce = debounce(async (props: any) => {
        await this.validate(props);
    }, 300);

    setFieldValue = async (field: string, value: any) => {
        await this.props.setFieldValue(field, value);
        await this.validateDebounce({action: this.saveActionType(), data: this.props.values, componentConfig: this.props.componentConfig});
    }

    validate = (props: {action: string, data: object, componentConfig: ComponentConfigType}) => {
        return new Promise(async (resolve) => {
            if (this.props.actionValidation) {
                const validationMessages = (await this.props.actionValidation(props)) || null;
                const valid = isValid(validationMessages);
                const isValidState = this.state.isValid;
                isValidState[props.action] = valid;
                this.setState({validationMessages, isValid: isValidState}, () => {
                    resolve(valid);
                });
            } else {
                const isValidState = this.state.isValid;
                isValidState[props.action] = true;
                this.setState({validationMessages: null, isValid: isValidState}, () => {
                    resolve(true);
                });
            }
        });
    }

    render() {
        const props = {
            ...this.props,
            reloadForm: this.reloadForm,
            pristineForm: this.pristineForm,
            loadForm: this.loadForm,
            saveForm: this.saveForm,
            setInitialValues: this.setInitialValues,
            setFormValues: this.setFormValues,
            setFieldValue: this.setFieldValue,
            deleteFrom: this.deleteForm,
            getFieldProps: this.getFieldProps,
            getFieldsProps: this.getFieldsProps,
            processAction: this.processAction,
            isLoading: this.isLoading(),
            saveButton: this.saveButton,
            newButton: this.newButton,
            deleteButton: this.deleteButton,
            isValid: this.state.isValid,
            validationMessages: this.state.validationMessages,
        }
        let beforeUnloadPrompt = null;
        if (this.state.beforeUnloadPrompt) {
            let beforeUnloadPromptProps = {
                ...this.props,
                isLoading: this.props.isLoading || this.state.isLoading,
                resolve: async (reason: any) => {
                    if (reason === true) {
                        await this.saveForm();
                    } else {
                        await this.props.setFormValues(this.props.initialValues);
                    }
                    this.state.beforeUnloadPrompt.resolve(reason);
                    await this.setState({beforeUnloadPrompt: null});
                },
                reject: async (reason: any) => {
                    console.error(reason);
                    this.state.beforeUnloadPrompt.reject(reason);
                    await this.setState({beforeUnloadPrompt: null});
                }
            }
            if (!this.props.beforeUnloadPrompt) {
                beforeUnloadPrompt = <AskBeforeUnload
                    {...beforeUnloadPromptProps}
                />;
            } else {
                if (this.props.beforeUnloadPrompt.type) {
                    beforeUnloadPrompt = <this.props.beforeUnloadPrompt.type
                        {...this.props.beforeUnloadPrompt.props}
                        {...beforeUnloadPromptProps}
                    />;
                } else if (this.props.beforeUnloadPrompt instanceof Function) {
                    beforeUnloadPrompt = <this.props.beforeUnloadPrompt
                        {...beforeUnloadPromptProps}
                    />
                }
            }
        }
        let beforeDeletePrompt = null;
        if (this.state.beforeDeletePrompt) {
            let beforeDeletePromptProps = {
                ...this.props,
                isLoading: this.props.isLoading || this.state.isLoading,
                resolve: async (reason: any) => {
                    if (reason === true) {
                        await this.saveForm();
                    }
                    this.state.beforeDeletePrompt.resolve(reason);
                    await this.setState({beforeDeletePrompt: null});
                },
                reject: async (reason: any) => {
                    console.error(reason);
                    this.state.beforeDeletePrompt.reject(reason);
                    await this.setState({beforeDeletePrompt: null});
                }
            }
            if (!this.props.beforeDeletePrompt) {
                beforeDeletePrompt = <AskBeforeDelete
                    {...beforeDeletePromptProps}
                />;
            } else {
                if (this.props.beforeDeletePrompt.type) {
                    beforeDeletePrompt = <this.props.beforeDeletePrompt.type
                        {...this.props.beforeDeletePrompt.props}
                        {...beforeDeletePromptProps}
                    />;
                } else if (this.props.beforeDeletePrompt instanceof Function) {
                    beforeDeletePrompt = <this.props.beforeDeletePrompt
                        {...beforeDeletePromptProps}
                    />
                }
            }
        }
        let message = this.state.message as ModalProps;
        return (
            <Fragment>
                {beforeUnloadPrompt}
                {beforeDeletePrompt}
                {message && <Portal>
                    <Modal
                        {...message}
                        state="on"
                        buttons={[
                            {
                                text: 'Ok',
                                onClose: true,
                                focus: true,
                                props: {
                                    timeout: this.state.message.timeout !== undefined ? this.state.message.timeout / 1000 : this.state.message.timeout,
                                    disableBeforeTimeout: false,
                                    color: "default"
                                },
                                onClick: async () => {
                                    await this.setState({message: null});
                                }
                            }
                        ]}
                    />
                </Portal>}
                <ComponentLoader
                    ref={(loader) => this.loader = loader}
                    componentName={this.props.componentName}
                    componentId={this.props[this.props.componentConfig.idfield]}
                    filter={this.props.filter}
                    view={this.props.view}
                    onBeforeLoad={this.onBeforeLoad}
                    onLoadCompleted={this.onLoadCompleted}
                    componentAs="serverValues"
                    loader={false}
                >
                    <PropsProxy ref={this.props.innerRef} {...props}>
                        {this.props.children}
                    </PropsProxy>
                </ComponentLoader>
            </Fragment>
        );
    }
}

class DefaultComponentEditor extends React.Component<ComponentValuesProps&{editorIsLoading?: boolean}, any> {
    render() {
        if (this.props.editorIsLoading) {
            return null;
        }
        return <ComponentValues {...this.props}>
            {this.props.children}
        </ComponentValues>;
    }
}

type ConnectedComponentEditorPropsType = Omit<ComponentEditorPropsType, 'dirty'> & {
    innerRef: React.Ref<DefaultComponentEditor>,
    setContent?: (editor: string, props: object) => void,
    setParams?: (props: object) => void,
    initialValues?: any|((props: ComponentEditorPropsType) => any),
    pristineValues?: any|((props: ComponentEditorPropsType) => any),
    dirty?: boolean|((props: ComponentEditorPropsType) => boolean),
};

const ConnectedComponentEditor = connectAdvanced((dispatch: (arg: any) => any) => {
        const bindActions = bindActionCreators({
            setFieldValue,
            setFormValues,
            setPristineValues,
            setInitialValues,
            setEditorConfig,
        }, dispatch);
        return (store: any, nextOwnProps: ConnectedComponentEditorPropsType) => {
            nextOwnProps = {...nextOwnProps};
            const componentEditor = nextOwnProps.editorContext || nextOwnProps.componentName;
            if (store && store.editors[componentEditor] === undefined) {
                try {
                    let initialState = nextOwnProps.initialValues;
                    if (initialState instanceof Function) {
                        initialState = initialState(nextOwnProps);
                    }
                    initialState = initialState || {};
                    addEditor(componentEditor, createEditorReducer(componentEditor, initialState));
                    setTimeout(() => {
                        dispatch({type: REDUX_EDITOR_INIT});
                    }, 0);
                } catch (e) {
                    console.error(e);
                }
                return {editorIsLoading: true};
            }
            const values = store.editors[componentEditor].values;
            let dirty = store.editors[componentEditor].dirty;
            let pristineValues = store.editors[componentEditor].pristineValues;
            let initialValues = store.editors[componentEditor].initialValues;
            const _setEditorConfig = (config: ComponentConfigType) => {
                dispatch(setEditorConfig(componentEditor, config));
            }

            if (nextOwnProps.pristineValues instanceof Function) {
                pristineValues = nextOwnProps.pristineValues({...nextOwnProps, values, initialValues, pristineValues});
            }
            if (nextOwnProps.initialValues instanceof Function) {
                initialValues = nextOwnProps.initialValues({...nextOwnProps, values, initialValues, pristineValues});
            }
            if (nextOwnProps.dirty instanceof Function) {
                // @ts-ignore
                dirty = nextOwnProps.dirty({...nextOwnProps, initialValues, values, dirty});
            } else if (nextOwnProps.dirty !== undefined) {
                dirty = nextOwnProps.dirty;
            }
            if (!nextOwnProps.loadForm && nextOwnProps.setContent) {
                nextOwnProps.loadForm = ({data, componentConfig}: {data?: any, componentConfig: ComponentConfigType}) => {
                    if (data?.[componentConfig.idfield]) {
                        nextOwnProps.setContent(componentEditor, {
                            [componentConfig.idfield]: data?.[componentConfig.idfield]
                        });
                    } else {
                        nextOwnProps.setContent(componentEditor, {});
                    }
                }
            }
            /*const componentConfig = nextOwnProps.componentConfig || {};
            const fielddefs = componentConfig.fielddefs || {};
            const exists = componentConfig.idfield ? values[componentConfig.idfield] : componentConfig.objecttype === "singleton";
            const componentName = componentConfig.componentname;
            const id = values[componentConfig.idfield];
            const asyncValues = new Proxy(values, {
                get: function (oTarget, sKey) {
                    if (get(oTarget, sKey)) {
                        return Promise.resolve(get(oTarget, sKey));
                    } else if (exists && fielddefs[sKey.split(".")[0]]) {
                        return new Promise((resolve) => {
                            loadComponentById(componentName, id, [sKey]).then((values) => resolve(get(values, sKey)));
                        });
                    }
                    return undefined;
                },
                set: function (oTarget, sKey, vValue) {
                    if (sKey in oTarget) { return false; }
                    return oTarget.setItem(sKey, vValue);
                },
                deleteProperty: function (oTarget, sKey) {
                    if (!sKey in oTarget) { return false; }
                    return oTarget.removeItem(sKey);
                },
                enumerate: function (oTarget, sKey) {
                    return Object.keys(fielddefs);
                },
                ownKeys: function (oTarget, sKey) {
                    return Object.keys(fielddefs);
                },
                has: function (oTarget, sKey) {
                    return sKey in oTarget || oTarget.hasItem(sKey);
                },
                defineProperty: function (oTarget, sKey, oDesc) {
                    if (oDesc && 'value' in oDesc) { oTarget.setItem(sKey, oDesc.value); }
                    return oTarget;
                },
                getOwnPropertyDescriptor: function (oTarget, sKey) {
                    var vValue = oTarget.getItem(sKey);
                    return vValue ? {
                        value: vValue,
                        writable: true,
                        enumerable: true,
                        configurable: false
                    } : undefined;
                },
            });*/
            return {
                user: store.app.user,
                menu: store.app.template.menu,
                innerRef: nextOwnProps.innerRef,
                ...bindEditorActions(componentEditor, bindActions),
                setEditorConfig: _setEditorConfig,
                //asyncValues,
                values,
                componentName: nextOwnProps.componentName,
                componentEditor,
                selection: values,
                ...nextOwnProps,
                pristineValues,
                initialValues,
                dirty,
            };
        }
    }
, {forwardRef: true})(DefaultComponentEditor);

type PropsType = ComponentEditorPropsType&{componentConfig: ComponentConfigType, setContent?: () => void};

const ComponentDataLoader = React.forwardRef<DefaultComponentEditor, PropsType>((props, ref) => {
    let next: any = {};
    if (!props.loadForm && !props.setContent) {
        const propsId = props[props.componentConfig.idfield];
        const [id, setId] = useState(propsId);
        useEffect(() => {
            setId(props[props.componentConfig.idfield]);
        }, [props[props.componentConfig.idfield]]);
        const loadForm = useCallback(({data, componentConfig}) => {
            if (data) {
                setId(data[componentConfig.idfield]);
            }
        }, []);
        next[props.componentConfig.idfield] = id;
        next.loadForm = loadForm;
    }
    return <ConnectedComponentEditor innerRef={ref} {...next} {...props} />;
});

const ComponentEditor = React.forwardRef<DefaultComponentEditor, ComponentEditorPropsType>((props, ref) => {
    return <EditorErrorBoundary>
        <ComponentConfig componentName={props.componentName}>
            <ComponentDataLoader ref={ref} {...props} />
        </ComponentConfig>
    </EditorErrorBoundary>;
});

ComponentEditor.displayName = "ComponentEditor";

export default ComponentEditor;